import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDeleteConfirm } from '../../hooks/useDeleteConfirm';
import { getAppLocale } from '../../utils/locale';
import { useMoney } from '../../hooks/useMoney';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import RichTextEditor from '../ui/RichTextEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from '../../services/PlanningApiClient';
import { loadStationeryItems } from '../StationeryTracker';
import { loadVendorData } from '../../data/VendorLoader';
import { ParsedVendor } from '../../data/vendorData';
import { uploadImage, deleteAttachment, resolveMediaUrl, getAttachments, type Attachment } from '../../services/uploadService';
import { useNotifications } from '../NotificationProvider';
import { generateId, useFormSaveEvent, useFormDeleteEvent, firstMissingField, useClearErrorsOnEdit } from './_shared';

interface StationeryItemFormPageProps {
  itemId?: string | null;
  onSave: () => void;
  onCancel: () => void;
  onNameLoad?: (name: string) => void;
  onArchivedLoad?: (archived: boolean) => void;
}

// Free-text Designer/Print Vendor fields double as a vendor-directory
// search — picking a suggestion links the real vendor_id (loose reference,
// no FK, same convention as budget_items.vendor_id) alongside the name, so
// the same vendor can be reused/deduped across items instead of retyped.
const VendorPickerInput: React.FC<{
  label: string;
  value: string;
  vendorId?: string;
  vendors: ParsedVendor[];
  onChange: (name: string, vendorId: string | undefined) => void;
  placeholder: string;
  error?: string;
}> = ({ label, value, vendorId, vendors, onChange, placeholder, error }) => {
  const [open, setOpen] = useState(false);
  const filtered = value.trim()
    ? vendors.filter(v => v.name.toLowerCase().includes(value.toLowerCase())).slice(0, 8)
    : [];

  return (
    <div className="form-group sfp-vendor-picker-wrap">
      <label>{label}</label>
      <input
        type="text"
        className={`form-input${error ? ' has-error' : ''}`}
        value={value}
        onChange={e => { onChange(e.target.value, undefined); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {vendorId && <span className="sfp-vendor-linked" title="Linked to a vendor record">✓ linked</span>}
      {error && <span className="form-error-text">{error}</span>}
      {open && filtered.length > 0 && (
        <ul className="sfp-vendor-dropdown">
          {filtered.map(v => (
            <li key={v.id} onMouseDown={() => { onChange(v.name, v.id); setOpen(false); }}>
              {v.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const StationeryItemFormPage: React.FC<StationeryItemFormPageProps> = ({ itemId, onSave, onCancel, onNameLoad, onArchivedLoad }) => {
  const money = useMoney();
  const { showSuccess, showInfo, showError } = useNotifications();
  const [form, setForm] = useState({
    name: '', category: 'invitation' as string, quantityNeeded: 0,
    costPerUnit: 0, designer: '', designerVendorId: undefined as string | undefined,
    printVendor: '', printVendorVendorId: undefined as string | undefined,
    dueDate: '', notes: '',
  });
  const [isExisting, setIsExisting] = useState(false);
  const [archived, setArchived] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [vendors, setVendors] = useState<ParsedVendor[]>([]);
  const [images, setImages] = useState<Attachment[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // A brand-new item has no real id yet — uploads still need a stable
  // entity_id to attach to, so a temp one is minted up front and reused
  // for every upload until the item is actually saved.
  const tempIdRef = useRef<string>(`new-${generateId()}`);
  const entityId = itemId ?? tempIdRef.current;

  useEffect(() => {
    void loadVendorData().then(setVendors).catch(() => {});
  }, []);

  useEffect(() => {
    setImagesLoading(true);
    getAttachments('stationery', entityId)
      .then(setImages)
      .catch(() => setImages([]))
      .finally(() => setImagesLoading(false));
  }, [entityId]);

  useEffect(() => {
    if (!itemId) { setIsExisting(false); setArchived(false); onArchivedLoad?.(false); return; }
    const applyItem = (existingItem: Record<string, any>) => {
      setForm({
        name: existingItem.name || '',
        category: existingItem.category || 'invitation',
        quantityNeeded: existingItem.quantityNeeded || 0,
        costPerUnit: existingItem.costPerUnit || 0,
        designer: existingItem.designer || '',
        designerVendorId: existingItem.designerVendorId || existingItem.assigned_to_vendor_id || undefined,
        printVendor: existingItem.printVendor || '',
        printVendorVendorId: existingItem.printVendorVendorId || existingItem.vendor_vendor_id || undefined,
        dueDate: existingItem.dueDate || '',
        notes: existingItem.notes || '',
      });
      setIsExisting(true);
      setArchived(!!existingItem.archived);
      onArchivedLoad?.(!!existingItem.archived);
      if (existingItem.name) onNameLoad?.(existingItem.name);
    };
    apiGet<any[] | { items: any[] }>(planningPath('/stationery'))
      .then(res => {
        const items: any[] = Array.isArray(res) ? res : (res as any).items ?? [];
        const found = items.find((i: any) => i.id === itemId);
        if (found) { applyItem(found); return; }
        throw new Error('not found in api');
      })
      .catch(() => {
        const cached = loadStationeryItems();
        const existingItem = cached.find(item => item.id === itemId);
        if (existingItem) applyItem(existingItem as any);
      });
  }, [itemId]);

  const handleSave = async () => {
    const missing = firstMissingField([{ label: 'Name', value: form.name }]);
    if (missing) { showError(missing); return; }
    if (isExisting && itemId) {
      await apiPut(planningPath(`/stationery/${itemId}`), { ...form, id: itemId });
    } else {
      const newItem = {
        ...form, id: generateId(), status: 'not-started', quantityPrinted: 0,
        designFileLink: '', createdAt: new Date().toISOString(),
      };
      await apiPost(planningPath('/stationery'), newItem);
    }
    onSave();
  };

  // Archive is the only action reachable via the header's "Archive" button
  // (dispatches the generic form-delete event) — real permanent delete is a
  // separate, distinct action only offered once the item is already archived
  // (see 'stationery-item-permanent-delete' below), matching VendorTable's
  // archive-first pattern.
  const handleConfirmArchive = () => {
    if (!itemId) return;
    setConfirmArchive(false);
    apiPut(planningPath(`/stationery/${itemId}`), { archived: true })
      .then(() => showInfo(`"${form.name}" archived — not permanently deleted. You can restore it from Stationery & Branding.`, { persistent: true }))
      .catch(() => showError('Failed to archive item'));
    onSave();
  };

  const handleRestore = () => {
    if (!itemId) return;
    apiPut(planningPath(`/stationery/${itemId}`), { archived: false })
      .then(() => showSuccess(`"${form.name}" restored`))
      .catch(() => showError('Failed to restore item'));
    onSave();
  };

  // Delete forever is only offered once the item is archived (Header shows the
  // button only then). The dialog stays open with the reason if it fails.
  const forever = useDeleteConfirm<string>({
    record: 'stationery item',
    perform: id => apiDelete(planningPath(`/stationery/${id}`)),
    onDone: () => { showSuccess(`"${form.name}" deleted`, { persistent: true }); onSave(); },
  });

  // The save above is awaited and throws on failure; the shared hook then shows
  // a 422's field errors under their fields (the API's names for them) or a toast.
  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the stationery item.' });
  useClearErrorsOnEdit(form, fieldErrors, { designer: 'designer', printVendor: 'printVendor' });
  useFormDeleteEvent(() => setConfirmArchive(true));

  useEffect(() => {
    if (!itemId) return;
    const onRestore = () => handleRestore();
    const onPermanentDelete = () => forever.request(itemId);
    window.addEventListener('stationery-item-restore', onRestore);
    window.addEventListener('stationery-item-permanent-delete', onPermanentDelete);
    return () => {
      window.removeEventListener('stationery-item-restore', onRestore);
      window.removeEventListener('stationery-item-permanent-delete', onPermanentDelete);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, form.name]);

  const handleImageFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setImageUploading(true);
    try {
      const uploads = await Promise.all(
        Array.from(files).map((file, i) =>
          uploadImage(file, { entityType: 'stationery', entityId, label: form.name || 'Design image', sortOrder: images.length + i })
        )
      );
      setImages(prev => [...prev, ...uploads]);
    } catch (err) {
      console.error('Design image upload failed:', err);
    } finally {
      setImageUploading(false);
    }
  }, [entityId, form.name, images.length]);

  const handleRemoveImage = useCallback(async (att: Attachment) => {
    try { await deleteAttachment(att.id); } catch { /* ignore */ }
    setImages(prev => prev.filter(a => a.id !== att.id));
  }, []);

  return (
    <div className="detail-layout">
      <div className="inline-form-page">
        {archived && (
          <div className="entity-state-banner entity-state-banner--locked">
            Archived — restore it from the header to edit further, or delete it permanently
          </div>
        )}
        <div className="form-row">
          <FormInput label="Item Name" type="text" value={form.name}
            error={fieldErrors.errorFor('name')}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Wedding Invitation Suite" className="sfp-form-group-flex2" required />
          <FormSelect label="Category" value={form.category}
            error={fieldErrors.errorFor('category')}
            onChange={e => setForm({ ...form, category: e.target.value })}
            options={[
              { value: 'invitation', label: 'Invitation' }, { value: 'programme', label: 'Programme' },
              { value: 'signage', label: 'Signage' }, { value: 'menu-card', label: 'Menu Card' },
              { value: 'seating', label: 'Seating' }, { value: 'digital', label: 'Digital' },
              { value: 'other', label: 'Other' },
            ]} />
        </div>
        {/* Vendors come before cost — cost per unit is normally the print
            vendor's quote, so asking for it before either vendor is picked
            was backwards. */}
        <div className="form-section">
          <p className="form-section-title">Vendors & Cost</p>
          <div className="form-row">
            <VendorPickerInput
              label="Designer"
              error={fieldErrors.errorFor('designer')}
              value={form.designer}
              vendorId={form.designerVendorId}
              vendors={vendors}
              onChange={(name, vendorId) => setForm(prev => ({ ...prev, designer: name, designerVendorId: vendorId }))}
              placeholder="Search vendors or type a name…"
            />
            <VendorPickerInput
              label="Print Vendor"
              error={fieldErrors.errorFor('printVendor')}
              value={form.printVendor}
              vendorId={form.printVendorVendorId}
              vendors={vendors}
              onChange={(name, vendorId) => setForm(prev => ({ ...prev, printVendor: name, printVendorVendorId: vendorId }))}
              placeholder="Search vendors or type a name…"
            />
          </div>
          <div className="form-row">
            <FormInput label="Quantity Needed" type="number" value={form.quantityNeeded}
              error={fieldErrors.errorFor('quantityNeeded')}
              onChange={e => setForm({ ...form, quantityNeeded: parseInt(e.target.value) || 0 })} min={0} />
            <FormInput label={`Cost per Unit (${money.unit})`} type="text" inputMode="numeric" value={form.costPerUnit > 0 ? form.costPerUnit.toLocaleString(getAppLocale()) : ''}
              error={fieldErrors.errorFor('costPerUnit')}
              onChange={e => setForm({ ...form, costPerUnit: parseFloat(e.target.value.replace(/,/g, '')) || 0 })} placeholder="0" />
          </div>
          <div className="form-row">
            <FormInput label="Due Date" type="date" value={form.dueDate}
              error={fieldErrors.errorFor('dueDate')}
              onChange={e => setForm({ ...form, dueDate: e.target.value })} />
          </div>
        </div>
        <RichTextEditor label="Notes" value={form.notes}
          error={fieldErrors.errorFor('notes')}
          onChange={val => setForm({ ...form, notes: val })} rows={3}
          placeholder="Additional notes..." />
      </div>

      <aside className="detail-sidebar">
        <section className="side-section">
          <h4>Design Images</h4>
          {imagesLoading && <p className="form-hint">Loading…</p>}
          {images.length > 0 && (
            <div className="sfp-image-grid">
              {images.map(att => (
                <div key={att.id} className="sfp-image-thumb">
                  <img src={resolveMediaUrl(att.url)} alt={att.label || 'Design'} />
                  <button type="button" className="sidebar-photo-remove-icon" onClick={() => handleRemoveImage(att)} title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}
          <label
            className="sidebar-photo-upload"
            onClick={e => { e.preventDefault(); imageInputRef.current?.click(); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); void handleImageFiles(e.dataTransfer.files); }}
          >
            <span className="sidebar-photo-upload-btn">
              {imageUploading ? 'Uploading…' : '+ Add Image'}
            </span>
          </label>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            className="u-hidden"
            onChange={e => { void handleImageFiles(e.target.files); e.target.value = ''; }}
          />
        </section>

        <section className="side-section">
          <h4>Cost Summary</h4>
          <div className="stat-row">
            <span>Quantity</span>
            <span>{form.quantityNeeded.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span>Cost per unit</span>
            <span>{money.format(form.costPerUnit)}</span>
          </div>
          <div className="stat-row">
            <span>Total</span>
            <span>{money.format(form.costPerUnit * form.quantityNeeded)}</span>
          </div>
        </section>
      </aside>
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={handleConfirmArchive}
        title="Archive this item?"
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the Archived filter."
        confirmLabel="Archive"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this item forever?"
        subject={form.name}
        description="This cannot be undone."
        consequences={['The item and its notes', 'Its design files and photos']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};
